import { useMatch, Link } from "react-router-dom";

import SmallFooter from "./SmallFooter";
import { getDynamicLink } from "../../../auxiliar/auxFunctions";
import useQueryParams from "../../../auxiliar/customHooks/useQueryParams";
import useIsStandalone from "../../../auxiliar/customHooks/useIsStandalone";

const Footer = ({ customDisplay = false }) => {
    const isStandalone = useIsStandalone();
    const params = useQueryParams();

    const landing = useMatch("/landing-page");
    const agenda = useMatch("/agenda");
    const reservationAgenda = useMatch("/reservation/agenda");
    const reservation = useMatch("/reservation/*");

    if (customDisplay || (!landing && !agenda && !reservationAgenda))
        return (
            <>
                <footer className="hidden md:block bg-gradient-to-r from-primary/5 via-primary/5 to-secondary/5 border-t border-primary/10 mt-10">
                    <div className="max-w-7xl mx-auto px-6 pb-5 pt-10">
                        {/* Main Footer Content */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                            {/* Brand Section */}
                            <div className="md:col-span-1">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="bg-primary/10 p-2 rounded-xl shadow-lg">
                                        <img
                                            src={require("../../../media/icon/icon_64.png")}
                                            alt="MiFisio Logo"
                                            className="w-6 h-6"
                                        />
                                    </div>
                                    <h3 className="text-xl font-bold text-primary">
                                        MiFisio Agenda
                                    </h3>
                                </div>
                                <p className="text-base-content/70 text-sm leading-relaxed max-w-md">
                                    Plataforma líder en gestión de citas, historiales y cobros para
                                    fisioterapeutas. Conectando profesionales de la salud con sus
                                    pacientes.
                                </p>
                            </div>

                            {/* Navigation Links */}
                            <div className="md:col-span-1">
                                <h4 className="text-base font-semibold text-primary mb-4">
                                    Enlaces útiles
                                </h4>
                                <nav className="space-y-3">
                                    <Link
                                        className="block text-base-content/70 hover:text-primary transition-all duration-200 text-sm font-medium relative group"
                                        to={getDynamicLink(
                                            reservation ? "/reservation" : "",
                                            "about-us",
                                            params,
                                        )}
                                    >
                                        Sobre nosotros
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
                                    </Link>
                                    <a
                                        className="block text-base-content/70 hover:text-primary transition-all duration-200 text-sm font-medium relative group"
                                        href="mailto:soporte@mifisioagenda.com"
                                    >
                                        Contacto
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
                                    </a>
                                    <Link
                                        className="block text-base-content/70 hover:text-primary transition-all duration-200 text-sm font-medium relative group"
                                        to={getDynamicLink(
                                            reservation ? "/reservation" : "",
                                            "privacy-policy",
                                            params,
                                        )}
                                    >
                                        Política de privacidad
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
                                    </Link>
                                    <Link
                                        className="block text-base-content/70 hover:text-primary transition-all duration-200 text-sm font-medium relative group"
                                        to={getDynamicLink(
                                            reservation ? "/reservation" : "",
                                            "health-check",
                                            params,
                                        )}
                                    >
                                        Estado de la aplicación
                                        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full"></span>
                                    </Link>
                                </nav>
                            </div>

                            {/* Social Media */}
                            <div className="md:col-span-1">
                                <h4 className="text-base font-semibold text-primary mb-4">
                                    Síguenos
                                </h4>
                                <div className="flex space-x-4">
                                    <a
                                        href="https://www.youtube.com/@mifisioagenda"
                                        target={isStandalone ? "_self" : "_blank"}
                                        rel="noopener noreferrer"
                                        aria-label="Visitar nuestro canal de YouTube"
                                        className="group bg-base-100 p-3 rounded-full hover:bg-red-500 transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl border border-primary/20"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            className="fill-current text-base-content group-hover:text-white transition-colors duration-300"
                                        >
                                            <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"></path>
                                        </svg>
                                    </a>
                                    <a
                                        href="https://www.linkedin.com/company/mifisio-agenda"
                                        target={isStandalone ? "_self" : "_blank"}
                                        rel="noopener noreferrer"
                                        aria-label="Visitar nuestro perfil de LinkedIn"
                                        className="group bg-base-100 p-3 rounded-full hover:bg-blue-600 transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl border border-secondary/20"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            className="fill-current text-base-content group-hover:text-white transition-colors duration-300"
                                        >
                                            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                                        </svg>
                                    </a>
                                </div>
                                <p className="text-xs text-base-content/60 mt-4">
                                    Mantente al día con nuestras últimas actualizaciones y consejos.
                                </p>
                            </div>
                        </div>

                        {/* Bottom Bar */}
                        <div className="pt-4 border-t border-primary/10">
                            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                                <p className="text-sm text-base-content/60">
                                    © {new Date().getFullYear()} MFISIO SCHEDULA SL. Todos los
                                    derechos reservados.
                                </p>
                                <div className="flex items-center space-x-4 text-xs text-base-content/50">
                                    <span className="text-secondary font-medium">
                                        Hecho por y para fisios
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
                <SmallFooter variation="main" />
            </>
        );

    return null;
};

export default Footer;
